import type { PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/browser';
import app from 'Cloud/Application/app';
import config from 'Cloud/config';
import { isEnabled, renderPublicUpload, sendPublicUploadRadar } from 'Cloud/ui/PublicUpload';
import sha1 from 'js-sha1';
import { logger } from 'lib/logger';
import { itemV4ToV2 } from 'reactApp/api/helpers/apiV4Helpers';
import { IS_AUTO_TEST_HIDE, IS_PUBLIC_ALBUM, IS_PUBLIC_FOLDER, IS_WEBVIEW, PAID_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { startEditor } from 'reactApp/modules/editor/editor.module';
import { handleLoadFacesList } from 'reactApp/modules/faces/faces.saga';
import { getFacesList, getFacesListRequestState } from 'reactApp/modules/faces/faces.selectors';
import { getFeatureFaces } from 'reactApp/modules/features/features.selectors';
import {
    fixItemHomeLowcaseBug,
    getNameById,
    getParent,
    getWeblinkFromPublicId,
    isDocument,
    isFolder as checkIsFolder,
    isPreviewable,
} from 'reactApp/modules/file/utils';
import {
    forceShowDownloadAppButton,
    loadMorePublicFolderRequest,
    loadMorePublicFolderSuccess,
    loadMoreToFileRequest,
    loadPublicFileSuccess,
    loadPublicPage,
} from 'reactApp/modules/public/public.actions';
import { sendOpenPublicAnalytics } from 'reactApp/modules/public/public.analytics';
import { getPublicItemById, getPublicOwner, isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { routeChangeStart, routerStart } from 'reactApp/modules/router/router.module';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { GetStateResponse } from 'reactApp/modules/router/router.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { openMobileViewer as openMobileViewerAction } from 'reactApp/modules/viewer/viewer.module';
import { isReactMobilePublic } from 'reactApp/sections/MobilePublicPage/isReactMobilePublic';
import { renderDownloadAppModal } from 'reactApp/ui/Mobile/DownloadAppModal/DownloadAppModal.helpers';
import { formatExpires } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';
import { call, cancel, put, select, takeEvery } from 'redux-saga/effects';
import { IPublicApiFolder, IPublicFolder } from 'server/types/context/IFolder';

import { loadPublicAlbumMore, loadPublicAlbumPage } from './publicAlbum.saga';

const serverSideFolder = config.get('serverSideFolders') as IPublicApiFolder;
const editor = config.get('EDITOR');
const DOWNLOAD_APP_MODAL_SHOWN = 'downloadAppModalShown' as const;

export function* openMobileViewer() {
    const id = yield select(getCurrentRouteId);

    if (isReactMobilePublic) {
        yield put(openMobileViewerAction({ id }));
    }
}

function* openDownloadAppModal() {
    try {
        if (isReactMobilePublic && !sessionStorage.getItem(DOWNLOAD_APP_MODAL_SHOWN) && !IS_WEBVIEW && !IS_AUTO_TEST_HIDE) {
            yield call(renderDownloadAppModal);
            sessionStorage.setItem(DOWNLOAD_APP_MODAL_SHOWN, 'true');
        }
    } catch (error) {
        yield put(forceShowDownloadAppButton());
        logger.error(error);
    }
}

export function* sendPublicAnalytics(id: string) {
    const isOwn = yield select(isOwnPublic);
    const item = yield select(getItemById, id);
    const owner: ReturnType<typeof getPublicOwner> = yield select(getPublicOwner);

    const isFolder = checkIsFolder(item);
    const storage = yield select(getCurrentStorage);
    const isEditor = isPreviewable(item, storage) && isDocument(item);

    const faces = yield select(getFacesList);

    sendOpenPublicAnalytics({
        id_public: isOwn ? sha1(item.home) : id,
        id_file: isFolder ? undefined : id,
        id_folder: isFolder ? id : undefined,
        type_public: isFolder ? 'folder' : 'file',
        count_objects: isFolder ? item.count.all : 1,
        extension: isFolder ? 'None' : item?.ext?.toLowerCase() || '',
        owner: owner?.email,
        isStock: storage === EStorageType.stock,
        isEditor,
        have_faces: Boolean(faces?.length),
        count_faces: faces?.length,
        source: 'public',
    });
}

function* getPublicFolder(id: string) {
    if (id === '/') {
        return;
    }

    if (IS_PUBLIC_FOLDER && serverSideFolder?.weblink === id) {
        return itemV4ToV2(serverSideFolder, EStorageType.public);
    }

    if (IS_PUBLIC_FOLDER) {
        return yield Promise.resolve(app.loadFolder(id, EStorageType.public, 0, true));
    }
}

let expiresShowed = false;

function* showExpires(item) {
    const expires = item?.weblinkExpires;

    if (!expires || app.browser.isPhone() || expiresShowed) {
        return;
    }

    expiresShowed = true;

    yield put(
        showSnackbarAction({
            id: 'public-expires',
            closable: true,
            text: `Доступ по ссылке открыт до ${formatExpires(expires)}`,
            type: SnackbarTypes.time,
            closeTimeout: 5000,
        })
    );
}

/* eslint-disable complexity */
/* eslint-disable-next-line sonarjs/cognitive-complexity */
function* loadFolder({ payload: { storage, id: weblinkId } }: PayloadAction<GetStateResponse>) {
    if (storage !== EStorageType.public || IS_PUBLIC_ALBUM) {
        return;
    }

    const item = yield select(getPublicItemById, weblinkId);

    if (item?.isFolder === false) {
        // TODO: routing: возможно, нужно более явно дергать loadFolder, а не каждому чиху на routeChangeStart CLOUDWEB-13172
        return;
    }

    if (PAID_PUBLIC) {
        sendDwh({ eventCategory: 'public', action: 'open-paid', dwhData: { public_id: weblinkId } });
    }

    try {
        const isServerSideFile = serverSideFolder?.type === 'file';
        const isWeblinkEquals = serverSideFolder?.weblink === weblinkId;

        const id = IS_PUBLIC_FOLDER && isServerSideFile && isWeblinkEquals ? getParent(weblinkId) : weblinkId;
        const folder = yield getPublicFolder(id);

        // Если это файл в папке для просмотрщика, то если его нет в папке (он дальше 500), то пока возьмем его из serverSideFolder
        const fileNotExist = isServerSideFile && folder ? !folder.list?.find((item) => item.weblink === weblinkId) : false;

        if (fileNotExist && folder) {
            folder.list.push(serverSideFolder);
        }

        if (fileNotExist && folder?.weblink) {
            // В фоне доскролливаем до этого файла.
            yield put(loadMoreToFileRequest({ folderId: folder.weblink, fileId: id }));
        }

        if (IS_PUBLIC_FOLDER && folder) {
            yield put(loadPublicPage(folder));
            yield openMobileViewer();
            yield openDownloadAppModal();

            const isFacesFeature = yield select(getFeatureFaces);
            const facesRequestState = yield select(getFacesListRequestState);

            // тк loadFolder вызывается на каждую смену роута, а лица - на весь паблик, а не на каждую папку,
            // то проверяем, что лица еще не загружали
            if (isFacesFeature && !facesRequestState.isLoaded) {
                const publicId = getWeblinkFromPublicId(id) ?? '';
                yield handleLoadFacesList({ id: publicId });

                yield sendPublicAnalytics(publicId);
            }

            yield showExpires(folder);

            // todo: Унести рендер плашки загрузки из саги в реакт
            if (isEnabled()) {
                renderPublicUpload();
                sendPublicUploadRadar();
            }

            return;
        }

        // Одиночный паблик
        if (isServerSideFile) {
            const item = itemV4ToV2(serverSideFolder, EStorageType.public);
            const name = item.name || getNameById(item);

            yield showExpires(item);

            yield put(
                loadPublicFileSuccess({
                    ...item,
                    name,
                    home: item.home ? fixItemHomeLowcaseBug(item.home, name) : null,
                })
            );

            // Когда открывается одиночный паблик с эдитором, сторадж - editor
            // Поэтому надо добавить в сторадж эдитора айтем
            if (editor?.file === item.name) {
                yield put(startEditor({ item }));
            }

            yield sendPublicAnalytics(item.id);

            return;
        }
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

function* loadMoreToFile(action) {
    try {
        const { fileId, folderId } = action.payload;
        let folder = yield select(getPublicItemById, folderId);

        if (!folder.isFolder) {
            return;
        }
        let hasMoreToLoad = folder.hasMoreToLoad;
        let fileExist = false;
        while (true) {
            if (!hasMoreToLoad || fileExist) {
                break;
            }
            const offset = folder.count.loaded - 1; // Так как там один ручками добавленный файл (иначе апи пропустит в выдаче один файл)
            const response: IPublicFolder = yield Promise.resolve(app.loadFolder(folderId, EStorageType.public, offset, true));
            // Проверяем, есть ли этот файл в ответе.
            // Если есть, то выкидываем его из ответа, так как ранее уже добавили его.
            const list = response.list.filter((item) => item.weblink !== fileId);
            fileExist = response.list.length !== list.length;
            response.list = list;
            yield put(loadMorePublicFolderSuccess(response));
            folder = yield select(getPublicItemById, folderId);
            hasMoreToLoad = folder.hasMoreToLoad;
        }
    } catch (error) {
        yield cancel();
    }
}

let loading = false;

function* loadMore(action) {
    if (IS_PUBLIC_ALBUM) {
        yield loadPublicAlbumMore();
        return;
    }

    if (loading) {
        return;
    }

    try {
        const { id, offset } = action.payload;
        loading = true;
        const folder: IPublicFolder = yield Promise.resolve(app.loadFolder(id, EStorageType.public, offset, true));
        loading = false;
        yield put(loadMorePublicFolderSuccess(folder));

        return folder;
    } catch (error) {
        yield cancel();
        loading = false;
    }
}

export function* watchPublic() {
    yield takeEvery(loadMorePublicFolderRequest.toString(), loadMore);
    yield takeEvery(loadMoreToFileRequest.toString(), loadMoreToFile);
    yield takeEvery(routeChangeStart.toString(), loadFolder);
    yield takeEvery(routerStart.toString(), loadPublicAlbumPage);
}
